import {
  AcuityAppointmentTag,
  crmClient,
  LeadStatus,
  MixpanelClient,
  MixpanelEvent,
  onboardingClient,
  ProfileStatus,
  ProviderRole,
  reportMonitoredError,
  scheduleClient,
  ScheduledAppointment,
  userClient,
} from '@enaratech/funnel-helper';
import { DateTime } from 'luxon';
import Toast from 'src/components/Common/Toast/Toast';
import { RECORD_RECENT_BOOKING, SET_SSB_INFO, SSBFlowState } from 'src/contexts/ssb/types';
import { randomElementFromArray } from 'src/utils/array';
import { findAppointmentKeyForStep, getDataFromTimeSlotKey } from '../../appointments';
import { AppointmentCreatedActionParams } from '../types';
import { getPreviousAppointmentByRules } from './fetchStatusAction';

const SEQUENCE_RULE_ERROR =
  'Selected time must be after your previous appointment. Please pick another slot.';

export const createAppointmentAction = async ({
  context,
  setters,
  setState,
}: AppointmentCreatedActionParams) => {
  const {
    selectedTimeSlotKey,
    availableProviders,
    scheduledAppointments,
    recentlyBookedAppointments,
    stepsRef,
    user,
    routePath,
    selectedLanguage,
  } = context;

  const {
    setAvailableProviders,
    setIsSubmitting,
    setScheduledAppointments,
    setSelectedLocation,
    setSelectedTimeSlotKey,
    setThereIsNoAvailability,
  } = setters;

  if (!selectedTimeSlotKey) {
    return Toast.notification('error', 'Please, select date and time of the appointment');
  }

  setIsSubmitting(true);

  if (!availableProviders || !(availableProviders.length > 0) || !stepsRef.current) {
    const error = {
      message: 'We could not create the appointment',
      steps: stepsRef.current,
      availableProviders,
      selectedTimeSlotKey,
    };
    Toast.notification('error', error.message);
    return reportMonitoredError({
      error: JSON.stringify(error, null, 3),
    });
  }

  const currentStep = stepsRef.current.find((s) => s.active);

  if (!currentStep) {
    reportMonitoredError({
      error: `[createAppointmentAction] There is not a current step for this user. ${currentStep}`,
      isReportable: true,
    });
    return null;
  }

  // Idempotency guard: never create a second appointment for a step already covered by a recent
  // funnel-local booking. The backend can re-offer a just-booked step during the Acuity webhook
  // propagation window; without this a duplicate is created (observed: two IN-NS appointments
  // booked to different providers ~2 min apart). Skip the POST and advance the flow instead.
  const recentAppointmentKeys = Object.keys(recentlyBookedAppointments ?? {});

  if (findAppointmentKeyForStep(currentStep, recentAppointmentKeys)) {
    setIsSubmitting(false);
    setAvailableProviders(null);
    setSelectedTimeSlotKey(null);
    reportMonitoredError({
      error: `[createAppointmentAction] blocked duplicate booking for step ${currentStep.config.allowedIntention}-${currentStep.config.allowedSpecialty}; already in recent bookings [${recentAppointmentKeys.join(
        ', '
      )}]`,
      isReportable: true,
    });
    setState({ type: SET_SSB_INFO, payload: SSBFlowState.GoToNextStep });
    return null;
  }

  const { allowedSpecialty } = currentStep.config;
  const { dateTime, acuityCalendarIds, acuityAppointmentTypeId } =
    getDataFromTimeSlotKey(selectedTimeSlotKey);

  // Submit-time sequence guard: reject a slot that isn't strictly after the appointment this step
  // was anchored to. Reuse getPreviousAppointmentByRules — the SAME rule the calendar used to build
  // the displayed slots — so the guard and the calendar always agree. For a Group Class step the
  // anchor is the Medical appointment (not the latest of all prior appointments); for 1:1 steps it
  // is the last scheduled step, which preserves the canonical sequence ordering.
  const previousAppointment = getPreviousAppointmentByRules({
    scheduledAppointments,
    stepsRef,
    selectedLanguage,
  });

  if (previousAppointment) {
    const selected = DateTime.fromISO(dateTime);
    const prior = DateTime.fromISO(previousAppointment.datetime);
    if (selected.isValid && prior.isValid && selected <= prior) {
      setIsSubmitting(false);
      reportMonitoredError({
        error: `[createAppointmentAction] selected slot ${dateTime} is not after previous appointment ${previousAppointment.datetime}`,
        isReportable: true,
      });
      return Toast.notification('error', SEQUENCE_RULE_ERROR);
    }
  }

  const randomAcuityCalendarId = randomElementFromArray<number>(acuityCalendarIds);

  const randomProvider = availableProviders.filter(
    (ap) => ap.acuityCalendarId === randomAcuityCalendarId
  )[0];

  if (!randomProvider) {
    // The selected slot's calendar is not among the current step's available providers — the
    // selection is stale/mismatched. Abort instead of proceeding (which would dereference
    // undefined and crash, or book against the wrong step). The user re-selects a valid time.
    setIsSubmitting(false);
    setAvailableProviders(null);
    setSelectedTimeSlotKey(null);
    reportMonitoredError({
      error: `randomProvider is undefined. Data: ${JSON.stringify(
        { acuityCalendarIds, randomAcuityCalendarId, availableProviders },
        null,
        3
      )}`,
      isReportable: true,
    });
    return Toast.notification('error', 'Please re-select a time for this appointment.');
  }

  const appointmentCreated = await scheduleClient.createAppointment({
    calendarId: randomProvider.acuityCalendarId,
    appointmentTypeId: acuityAppointmentTypeId,
    dateTime,
    tag: AcuityAppointmentTag.FunnelAppSSB,
  });

  if (!appointmentCreated) {
    setIsSubmitting(false);

    return Toast.notification('error', 'Could not create appointment');
  }

  const { date, endTime, datetime, timezone, location, type } = appointmentCreated;

  if (allowedSpecialty === ProviderRole.MedicalProvider) {
    const userWasUpdated = await userClient.updateUser({
      initialAppointmentId: appointmentCreated.id,
    });

    if (userWasUpdated) {
      userClient.activateUser();
    }

    userClient.setProfileStatus(ProfileStatus.Exploring);

    crmClient.updateLead({
      uuid: user!.uuid,
      startDate: appointmentCreated.datetime,
      medicalProviderName: `${randomProvider.firstName} ${randomProvider.lastName}`,
    });

    onboardingClient.updateMemberStatus({
      crm: {
        leadStatus: LeadStatus.InitialBooked,
        bookingData: { datetime: appointmentCreated.datetime },
      },
    });

    MixpanelClient.setMetadata({ ssb: true });
  }

  MixpanelClient.setMetadata({
    initials: Object.keys(scheduledAppointments!).join(','),
  });

  MixpanelClient.trackEvent({
    event: MixpanelEvent.InputData,
    properties: {
      field: 'Appointment created',
      value: currentStep.details.specialtyName,
      source: routePath,
    },
  });
  const appointmentCode = type.match(/[A-Z]+(-[A-Z]+)+/g)![0];

  const newScheduledAppointment: ScheduledAppointment = {
    appointmentId: appointmentCreated.id,
    date,
    endTime,
    datetime,
    timezone,
    location,
    calendar: appointmentCreated.calendar,
  };

  const recentlyFetchedPreviousAppointments = {
    ...scheduledAppointments!,
    [appointmentCode]: newScheduledAppointment,
  };

  // Record the booking in funnel-local memory so subsequent backend reads (which may not yet
  // reflect the Acuity webhook) merge it back in until the backend catches up.
  setState({
    type: RECORD_RECENT_BOOKING,
    payload: { code: appointmentCode, appointment: newScheduledAppointment },
  });

  setAvailableProviders(null);
  setSelectedTimeSlotKey(null);
  setSelectedLocation(undefined);
  setScheduledAppointments(recentlyFetchedPreviousAppointments);
  setIsSubmitting(false);
  setThereIsNoAvailability(false);

  setTimeout(() => setState({ type: SET_SSB_INFO, payload: SSBFlowState.GoToNextStep }), 3000);
};
